import { lazy, Suspense, useEffect, useState, useRef } from "react";
import type { AdsReportRow } from "../ads-stats/useAdsStats.ts";
import type { QueueSummary } from "./types.ts";
import type { SelectionScope } from "./TriggerManagerDialog.tsx";
import { fmtDate, emptyScope } from "./types.ts";
import { request, errorMessage } from "./api.ts";
import { TriggerIcon } from "./ui.tsx";
import { AccessibleDialog } from "../../core/components/AccessibleDialog.tsx";
import "./triggers.css";
const Workspace = lazy(() =>
  import("./TriggerManagerDialog.tsx").then((m) => ({
    default: m.TriggerWorkspace,
  })),
);
export function selectionScope(
  rows: AdsReportRow[],
  accountFilter: string[] | null,
): SelectionScope | null {
  if (!rows.length) return null;
  const kind =
    rows[0].rowKind === "creative"
      ? "creative_set"
      : rows[0].rowKind === "campaigns"
        ? "campaign_set"
        : rows[0].rowKind === "ads"
          ? "ad_set"
          : null;
  if (!kind || rows.some((r) => r.rowKind !== rows[0].rowKind)) return null;
  const config = emptyScope();
  if (kind === "creative_set") {
    if (
      rows.some(
        (r) =>
          !r.creative_id ||
          ("creative_mapping_review" in r && r.creative_mapping_review),
      )
    )
      return null;
    config.creativeIds = [
      ...new Set(
        rows.flatMap((r) =>
          r.creative_id &&
          !("creative_mapping_review" in r && r.creative_mapping_review)
            ? [r.creative_id]
            : [],
        ),
      ),
    ];
    config.accountIds = accountFilter ?? [];
    if (!config.creativeIds.length) return null;
  } else {
    const refs = rows.flatMap((r) =>
      r.entityRef &&
      /^\d+$/.test(r.entityRef.id) &&
      /^\d+$/.test(r.entityRef.adAccountId)
        ? [{ id: r.entityRef.id, adAccountId: r.entityRef.adAccountId }]
        : [],
    );
    config.accountIds = [...new Set(refs.map((r) => r.adAccountId))];
    if (kind === "campaign_set") config.campaignRefs = refs;
    else config.adRefs = refs;
    if (refs.length !== rows.length) return null;
  }
  return {
    name:
      kind === "creative_set"
        ? "Крео з призначення"
        : kind === "campaign_set"
          ? "Вибрані кампанії"
          : "Вибрані оголошення",
    kind,
    config,
  };
}
export function TriggerFeature({
  selectedRows,
  accountFilter,
  executionId,
  onExecutionConsumed,
  onChanged,
  onSync,
}: {
  selectedRows: AdsReportRow[];
  accountFilter: string[] | null;
  executionId: string | null;
  onExecutionConsumed: () => void;
  onChanged: () => void;
  onSync: (ids: string[]) => void;
}) {
  const returnFocus = useRef<HTMLElement | null>(null);
  const [open, setOpen] = useState(false),
    [seed, setSeed] = useState<SelectionScope | null>(null),
    [details, setDetails] = useState<string | null>(null),
    [revision, setRevision] = useState(0);
  const [count, setCount] = useState<number | null>(null),
    [summary, setSummary] = useState<QueueSummary | null>(null),
    [error, setError] = useState("");
  useEffect(() => {
    if (executionId) {
      returnFocus.current = document.activeElement as HTMLElement;
      setSeed(null);
      setDetails(executionId);
      setOpen(true);
      onExecutionConsumed();
    }
  }, [executionId, onExecutionConsumed]);
  useEffect(() => {
    const abort = new AbortController();
    let inFlight = false;
    const load = async () => {
      if (inFlight || document.visibilityState === "hidden") return;
      inFlight = true;
      try {
        const [rules, status] = await Promise.all([
          request<{ totalCount: number }>(
            "/triggers?enabled=true&limit=1",
            "GET",
            undefined,
            undefined,
            abort.signal,
          ),
          request<QueueSummary>(
            "/trigger-queue/summary",
            "GET",
            undefined,
            undefined,
            abort.signal,
          ),
        ]);
        if (!abort.signal.aborted) {
          setCount(rules.totalCount);
          setSummary(status);
          setError("");
        }
      } catch (e) {
        if (!abort.signal.aborted) setError(errorMessage(e));
      } finally {
        inFlight = false;
      }
    };
    void load();
    const timer = setInterval(load, 60_000);
    document.addEventListener("visibilitychange", load);
    return () => {
      abort.abort();
      clearInterval(timer);
      document.removeEventListener("visibilitychange", load);
    };
  }, [revision]);
  const selection = selectionScope(selectedRows, accountFilter);
  const changed = () => {
    setRevision((v) => v + 1);
    onChanged();
  };
  return (
    <div className="trigger-entry">
      <button
        type="button"
        className="btn btn--secondary"
        onClick={() => {
          returnFocus.current = document.activeElement as HTMLElement;
          setSeed(null);
          setDetails(null);
          setOpen(true);
        }}
      >
        <TriggerIcon />
        Тригери
        {count !== null && <span className="trigger-entry-count">{count}</span>}
      </button>
      {selection && (
        <button
          type="button"
          className="btn btn--secondary"
          onClick={() => {
            setSeed(selection);
            setDetails(null);
            setOpen(true);
          }}
        >
          Призначити тригер ({selectedRows.length})
        </button>
      )}
      <button
        type="button"
        className="trigger-health"
        title={error || "Очікують серверні завдання перевірки умов тригерів після оновлення статистики. Це не кількість спрацьовувань чи вимкнень реклами."}
        onClick={() => {
          setSeed(null);
          setDetails(null);
          setOpen(true);
        }}
      >
        {error
          ? "Сервіс недоступний"
          : summary?.evaluation.pending
            ? summary.evaluation.pending + " перевірок очікують"
            : summary?.evaluation.lastCompletedAt
              ? "Перевірено " + fmtDate(summary.evaluation.lastCompletedAt)
              : summary?.mutationsEnabled
                ? summary.paused
                  ? "Чергу зупинено"
                  : "Черга працює"
                : "Dry-run · ще не перевірено"}
      </button>
      {open && (
        <Suspense
          fallback={
            <AccessibleDialog
              title="Тригери"
              description="Завантаження менеджера…"
              onClose={() => setOpen(false)}
            >
              <p role="status">Завантаження…</p>
            </AccessibleDialog>
          }
        >
          <Workspace
            returnFocus={returnFocus.current}
            onClose={() => setOpen(false)}
            onChanged={changed}
            seed={seed}
            initialExecution={details}
            summary={summary}
            onSync={onSync}
          />
        </Suspense>
      )}
    </div>
  );
}
